import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { Button, ContentCard, cn, Label } from '@/shared/ui';
import { useRejectQuote } from '../model/use-reject-quote';

export interface RejectQuoteFormProps {
  readonly quoteRequestId: string;
  readonly onRejected?: () => void;
}

const REASON_MAX_LENGTH = 500;
const CONFIRM_MESSAGE = '이 견적을 반려하시겠습니까? 반려 후에는 되돌릴 수 없습니다.';

/** 반려 사유 폼(견적 설계 §6-6) — 사유 1~500자, 확인창 뒤 반려. 고객은 내 견적에서 사유를 본다. */
export const RejectQuoteForm = ({
  quoteRequestId,
  onRejected,
}: RejectQuoteFormProps): ReactElement => {
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const errorId = `${reasonId}-error`;
  const { reject, isPending, fieldError, formError } = useRejectQuote();
  const message = fieldError ?? formError;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    // 빈 사유는 확인창 없이 칸 오류만 보여 준다(훅이 검증한다).
    if (reason.trim() !== '' && !window.confirm(CONFIRM_MESSAGE)) {
      return;
    }

    if (await reject({ quoteRequestId, reason })) {
      setReason('');
      onRejected?.();
    }
  };

  return (
    <ContentCard title="반려" className="mt-6 border-destructive/30">
      <form
        aria-label="반려"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonId} isRequired>
            반려 사유
          </Label>
          <textarea
            id={reasonId}
            rows={3}
            required
            maxLength={REASON_MAX_LENGTH}
            value={reason}
            disabled={isPending}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={message ? errorId : undefined}
            className={cn(
              'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              fieldError && 'border-destructive focus-visible:ring-destructive',
            )}
            placeholder="고객에게 그대로 보입니다."
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        {message ? (
          <p id={errorId} role="alert" className="text-sm text-destructive">
            {message}
          </p>
        ) : null}
        <Button type="submit" variant="destructive" isLoading={isPending} className="self-start">
          반려하기
        </Button>
      </form>
    </ContentCard>
  );
};
